import axios from 'axios';
import type { RadioStation, Tag } from '../types/radio';
import { logger } from '../utils/logger';
import dns from 'dns/promises';

/**
 * Fallback stations to use when the Radio-Browser API is down
 */
const FALLBACK_STATIONS = [
  {
    name: "BBC Radio 1",
    url: "https://stream.live.vc.bbcmedia.co.uk/bbc_radio_one",
    favicon: "https://static.bbci.co.uk/multimedia/images/1_16x9/bbc_radio_one.png",
    tags: "pop,hits,uk,public",
    country: "United Kingdom",
    countryCode: "GB",
    codec: "MP3",
    bitrate: 128
  },
  {
    name: "NPR News",
    url: "https://npr-ice.streamguys1.com/live.mp3",
    favicon: "https://media.npr.org/images/podcasts/primary/npr_generic_image_300.jpg",
    tags: "news,talk,public",
    country: "United States",
    countryCode: "US",
    codec: "MP3",
    bitrate: 128
  },
  {
    name: "Smooth Jazz 24/7",
    url: "https://smoothjazz.cdnstream1.com/2585_128.mp3",
    tags: "jazz,smooth,relaxing",
    country: "United States",
    countryCode: "US",
    codec: "MP3",
    bitrate: 128
  },
  {
    name: "ClassicFM",
    url: "https://media-ice.musicradio.com/ClassicFMMP3",
    favicon: "https://www.classicfm.com/assets_v4r/classic/img/favicon-196x196.png",
    tags: "classical,orchestral,relaxing",
    country: "United Kingdom",
    countryCode: "GB",
    codec: "MP3",
    bitrate: 128
  },
  {
    name: "SomaFM Groove Salad",
    url: "https://ice2.somafm.com/groovesalad-128-mp3",
    favicon: "https://somafm.com/img/groovesalad120.png",
    tags: "ambient,electronic,chill",
    country: "United States",
    countryCode: "US",
    codec: "MP3",
    bitrate: 128
  }
];

// Constants for API and caching
/**
 * Known Radio Browser hosts, without protocol.
 *
 * fi1 used to be here and no longer resolves at all — the name is gone from DNS,
 * so every request that happened to land on it failed with ERR_NAME_NOT_RESOLVED
 * and had to be retried against another host. Since the list is shuffled, that was
 * roughly a third of all requests paying double.
 *
 * The three below currently answer on the same machine; they are kept as separate
 * names so a future split is picked up without a code change.
 */
const FALLBACK_SERVERS = [
  "de1.api.radio-browser.info",
  "de2.api.radio-browser.info",
  "all.api.radio-browser.info"
];

// Modified cache durations for better performance
const CACHE_DURATIONS = {
  REALTIME: 30 * 1000,         // 30 seconds for real-time data (clicks, trends)
  DYNAMIC: 2 * 60 * 1000,      // 2 minutes for dynamic data (station metadata)
  STANDARD: 5 * 60 * 1000,     // 5 minutes for standard data
  STATIC: 15 * 60 * 1000,      // 15 minutes for static data
  COUNTRY: 10 * 60 * 1000      // 10 minutes for country-specific data
};

// Increased timeouts and retries for better reliability
const REQUEST_TIMEOUT = 10000;  // 10 seconds for initial request
const BATCH_SIZE = 100;         // Reduced batch size to avoid rate limiting
const STATION_STORAGE_CHUNK_SIZE = 100;
const MAX_LOCAL_STORAGE_BYTES = 5 * 1024 * 1024; // 5 MB typical browser quota
const MAX_STORAGE_CHUNK_COUNT = 100; // Avoid writing hundreds of large keys (stay within ~5 MB quota)
const REDUCED_STATION_CACHE_SIZE = 200;

type PersistedStation = Pick<RadioStation,
  'stationuuid' |
  'changeuuid' |
  'name' |
  'url' |
  'url_resolved' |
  'votes' |
  'lastchangetime' |
  'codec' |
  'bitrate' |
  'hls' |
  'lastcheckok' |
  'lastchecktime' |
  'ssl_error' |
  'clickcount' |
  'clicktrend'
> & Partial<Pick<RadioStation,
  'favicon' |
  'tags' |
  'country' |
  'countrycode' |
  'state' |
  'language' |
  'homepage' |
  'lastcheckoktime' |
  'clicktimestamp' |
  'geo_lat' |
  'geo_long'
>>;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface StatsResponse {
  stations: number;
  stations_broken: number;
  countries: number;
  languages: number;
  tags: number;
  clicks_last_hour: number;
  clicks_last_day: number;
  categories: number;
}

interface StationSearchParams {
  name?: string;
  nameExact?: boolean;
  country?: string;
  countrycode?: string;
  countryExact?: boolean;
  state?: string;
  stateExact?: boolean;
  language?: string;
  languageExact?: boolean;
  tag?: string;
  tagList?: string;
  tagExact?: boolean;
  codec?: string;
  bitrateMin?: number;
  bitrateMax?: number;
  offset?: number;
  limit?: number;
  order?: string;
  reverse?: boolean;
  hidebroken?: boolean;
}

interface CountryInfo {
  name: string;
  stationcount: number;
}

export class RadioBrowserService {
  private static instance: RadioBrowserService;
  private cache: Map<string, CacheEntry<any>> = new Map();
  private popularTagsCache: Tag[] = [];
  private currentServer: string | null = null;
  private serverList: string[] = [];
  private allStationsCache: RadioStation[] = [];
  private stationCache: Map<string, { data: RadioStation[]; timestamp: number }> = new Map();
  private userAgent = 'aurafy-radio/1.0 (WiGy Studios)'; // Consistent User-Agent
  private textEncoder?: TextEncoder;

  private constructor() {
    logger.info('🎵 Initializing Radio Browser Service...');
    this.initializeServers();
  }

  public static getInstance(): RadioBrowserService {
    if (!RadioBrowserService.instance) {
      RadioBrowserService.instance = new RadioBrowserService();
    }
    return RadioBrowserService.instance;
  }

  /**
   * Initialize the server list by querying DNS
   * or falling back to the predefined list
   */
  private async initializeServers(): Promise<void> {
    // Discovery below uses Node's `dns` module, which does not exist in a browser.
    // Bundled for the browser it resolves to an empty stub, so every call throws
    // and the code always ends up on the fallback list anyway — after logging an
    // SRV warning, a lookup failure and a red error on every single page load.
    // The outcome is identical without the noise, and without pretending a
    // browser can make a DNS query.
    if (typeof window !== 'undefined') {
      this.serverList = this.shuffleServers([...FALLBACK_SERVERS]);
      this.currentServer = this.serverList[0];
      return;
    }

    try {
      logger.info('🔍 Looking up Radio Browser API servers...');

      // First try SRV record approach (recommended in docs)
      try {
        const srvRecords = await dns.resolveSrv('_api._tcp.radio-browser.info');
        if (srvRecords && srvRecords.length > 0) {
          const servers = srvRecords.map(record => record.name);
          this.serverList = this.shuffleServers(servers);
          this.currentServer = this.serverList[0];
          logger.success(`✅ Found ${servers.length} Radio Browser API servers via SRV records`);
          return;
        }
      } catch (srvError) {
        logger.warning('⚠️ SRV lookup failed, trying A record fallback');
      }

      // Fallback to A record method
      const addresses = await dns.resolve4('all.api.radio-browser.info');
      if (addresses && addresses.length > 0) {
        const hostnames = await Promise.all(
          addresses.map(async (ip) => {
            try {
              const hostnames = await dns.reverse(ip);
              return hostnames[0];
            } catch {
              return null;
            }
          })
        );

        const validHostnames = hostnames
          .filter((h) => h !== null)
          .filter(h => h.includes('api.radio-browser.info'));

        if (validHostnames.length > 0) {
          this.serverList = this.shuffleServers(validHostnames);
          this.currentServer = this.serverList[0];
          logger.success(`✅ Found ${validHostnames.length} Radio Browser API servers via A records`);
          return;
        }
      }

      // Last resort: use hardcoded servers
      this.serverList = this.shuffleServers([...FALLBACK_SERVERS]);
      this.currentServer = this.serverList[0];
      logger.warning('⚠️ DNS lookup failed, using fallback servers');
    } catch (error) {
      logger.error('❌ Failed to lookup Radio Browser API servers');
      this.serverList = this.shuffleServers([...FALLBACK_SERVERS]);
      this.currentServer = this.serverList[0];
      logger.warning(`⚠️ Using fallback servers: ${this.serverList.join(', ')}`);
    }
  }

  /**
   * Shuffle an array using Fisher-Yates algorithm
   */
  private shuffleServers(servers: string[]): string[] {
    const result = [...servers];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  /**
   * Makes a request to the Radio Browser API
   * with automatic retry using a different server on failure
   */
  private async makeRequest<T>(endpoint: string, params: Record<string, any> = {}): Promise<T> {
    // Create the query string from params
    const queryParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        queryParams.append(key, String(value));
      }
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const path = `${endpoint}${queryString}`;

    // Try each server until one works
    let lastError: Error | null = null;
    const servers = [...this.serverList]; // Create a copy to avoid modifying the original

    for (let i = 0; i < servers.length; i++) {
      const server = servers[i];
      try {
        // Direct connection to the API without CORS proxies
        const url = `https://${server}/json${path}`;

        logger.info(`📡 Requesting: ${url}`);

        const requestConfig: { timeout: number; headers?: Record<string, string> } = {
          timeout: REQUEST_TIMEOUT
        };

        if (typeof window === 'undefined') {
          requestConfig.headers = {
            'User-Agent': this.userAgent
          };
        }

        const response = await axios.get(url, requestConfig);

        // If successful, move this server to the front of the list for future requests
        if (this.serverList.indexOf(server) !== 0) {
          this.serverList = [server, ...this.serverList.filter(s => s !== server)];
        }

        return response.data;
      } catch (error: unknown) {
        const err = error as Error;
        lastError = err;
        logger.warning(`⚠️ Request to ${server} failed: ${err.message}`);

        // If this is the last server, throw the error
        if (i === servers.length - 1) {
          throw lastError;
        }
      }
    }

    throw lastError || new Error('All servers failed');
  }

  private getCacheKey(endpoint: string, params: any = {}): string {
    // Special handling for different types of queries
    if (params.countrycode) {
      return `country:${params.countrycode}:${endpoint}:${JSON.stringify(params)}`;
    }
    if (params.language) {
      return `language:${params.language}:${endpoint}:${JSON.stringify(params)}`;
    }
    if (params.tagList) {
      return `tags:${params.tagList}:${endpoint}:${JSON.stringify(params)}`;
    }
    if (params.order) {
      return `sorted:${params.order}:${endpoint}:${JSON.stringify(params)}`;
    }
    return `${endpoint}:${JSON.stringify(params)}`;
  }

  private getCachedData<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() < cached.expiresAt) {
      // Don't log cache hits for stats queries to reduce noise
      if (!key.includes('stats')) {
        logger.info(`📦 Using cached data for: ${key}`);
      }
      return cached.data;
    }
    if (cached) {
      this.cache.delete(key);
    }
    return null;
  }

  private setCachedData<T>(key: string, data: T, duration: number = CACHE_DURATIONS.STANDARD): void {
    const now = Date.now();

    // Determine appropriate cache duration based on key type
    let cacheDuration = duration;
    if (key.startsWith('country:')) {
      cacheDuration = CACHE_DURATIONS.COUNTRY;
    } else if (key.startsWith('language:')) {
      cacheDuration = CACHE_DURATIONS.REALTIME; // Shorter cache for language searches
    } else if (key.includes('clickcount') || key.includes('clicktrend')) {
      cacheDuration = CACHE_DURATIONS.REALTIME;
    } else if (key.includes('tags:')) {
      cacheDuration = CACHE_DURATIONS.DYNAMIC;
    }

    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + cacheDuration
    });

    // Log cache operations in development
    if (process.env.NODE_ENV === 'development') {
      logger.info(`📦 Cached ${key} for ${cacheDuration / 1000}s`);
    }
  }

  private categorizeTag(tag: string): Tag['type'] {
    // Common music genre indicators
    if (tag.includes('music') ||
      tag.match(/^(pop|rock|jazz|classical|electronic|metal|folk|blues|country|rap|hip hop|reggae|soul|funk)$/)) {
      return 'genre';
    }

    // Language indicators
    if (tag.match(/^[a-z]{2,3}$/) ||
      tag.includes('language') ||
      tag.endsWith('ish') || // english, spanish, etc.
      tag.endsWith('ese')) { // japanese, chinese, etc.
      return 'language';
    }

    // Topic indicators
    if (tag.includes('news') ||
      tag.includes('talk') ||
      tag.includes('sport') ||
      tag.includes('weather') ||
      tag.includes('traffic')) {
      return 'topic';
    }

    return 'other';
  }

  private parseStationTags(station: RadioStation): RadioStation {
    if (typeof station.tags === 'string') {
      // Keep tags as a comma-separated string instead of converting to array
      station.tags = station.tags.split(',')
        .map(tag => tag.trim())
        .join(',');
    }
    return station;
  }

  private normalizeTag(tag: string): string | null {
    try {
      if (!tag) return null;

      // Remove hashtags, normalize whitespace, remove quotes
      const normalized = tag.trim()
        .toLowerCase()
        .replace(/^#/, '')
        .replace(/["']/g, '')
        .replace(/\s+/g, ' ');

      // Validate the normalized tag
      if (normalized.length < 2 || normalized.length > 50) {
        return null;
      }

      // Remove common noise words that don't add value as tags
      const noiseWords = ['radio', 'fm', 'am', 'the', 'a', 'an'];
      if (noiseWords.includes(normalized)) {
        return null;
      }

      return normalized;
    } catch (error) {
      logger.error('Failed to normalize tag:');
      return null;
    }
  }

  public async getStationStats(): Promise<any> {
    const cacheKey = 'stats';
    const cached = this.getCachedData<any>(cacheKey);
    if (cached) return cached;

    try {
      const stats = await this.makeRequest<any>('/stats');
      // Ensure categories data is present
      if (stats && !stats.categories && stats.tags) {
        // Categories is often missing in API response, use tags count as fallback
        stats.categories = stats.tags;
      }

      // Same correction getStats applies: report the catalogue we actually hold,
      // not Radio Browser's global total. Both methods share the 'stats' cache
      // key, so without this whichever page loaded last decided the number, and
      // the station count could differ between Home and Explore in one session.
      const catalogueCount = await this.getCatalogueCount();
      if (catalogueCount !== null && stats) stats.stations = catalogueCount;

      this.setCachedData(cacheKey, stats, CACHE_DURATIONS.REALTIME); // Short cache timeout
      return stats;
    } catch (error) {
      logger.error('Failed to fetch station statistics:');

      // Try to get real data from other API endpoints
      try {
        // Initialize an empty stats object
        const fallbackStats: Partial<StatsResponse> = {};

        // Use the actual length of stations we have in cache
        fallbackStats.stations = this.allStationsCache.length || 0;

        // Get actual countries count from API or cached countries
        try {
          const countries = await this.getCountries();
          fallbackStats.countries = countries.length || 0;
        } catch (e) {
          logger.warning('Could not get countries count');
        }

        // Get languages count from API or cache
        try {
          const languages = await this.getLanguages();
          fallbackStats.languages = languages.length || 0;
        } catch (e) {
          logger.warning('Could not get languages count');
        }

        // Get tags count from popular tags
        try {
          const tags = await this.getPopularTags(500);
          fallbackStats.tags = tags.length || 0;
          // Use tags as a fallback for categories if needed
          fallbackStats.categories = tags.length || 0;
        } catch (e) {
          logger.warning('Could not get tags count');
        }

        logger.info('Using fallback stats from cache and other endpoints');
        return fallbackStats;
      } catch (fallbackError) {
        logger.error('Failed to generate fallback stats:');
        // Return minimal stats to prevent UI errors
        return { stations: this.allStationsCache.length || 0 };
      }
    }
  }

  public async getTopStations(limit: number = 100, offset: number = 0, sortBy: string = 'clickcount'): Promise<RadioStation[]> {
    const cacheKey = this.getCacheKey('/stations/topclick', { limit, offset, sortBy });
    const cached = this.getCachedData<RadioStation[]>(cacheKey);
    if (cached) return cached;

    try {
      if (this.allStationsCache.length > 0) {
        // Apply appropriate sorting based on sortBy parameter
        const sorted = this.sortStations([...this.allStationsCache], sortBy, true)
          .slice(offset, offset + limit);

        this.setCachedData(cacheKey, sorted, CACHE_DURATIONS.REALTIME);
        return sorted;
      }

      const stations = await this.makeRequest<RadioStation[]>('/stations/search', {
        offset: offset.toString(),
        limit: Math.min(limit, BATCH_SIZE).toString(),
        hidebroken: true,
        order: sortBy,
        reverse: true
      });

      const processedStations = stations.map(station => ({
        ...station,
        clickcount: station.clickcount || 0,
        clicktrend: station.clicktrend || 0
      }));

      this.setCachedData(cacheKey, processedStations, CACHE_DURATIONS.REALTIME);
      return processedStations;
    } catch (error) {
      logger.error('Failed to fetch top stations:');
      return [];
    }
  }

  public async getStationsByCountryCode(countryCode: string, offset: number = 0, limit: number = 100, sortBy: string = 'clickcount'): Promise<RadioStation[]> {
    const normalizedCountryCode = countryCode.toUpperCase();
    const cacheKey = this.getCacheKey('stations/bycountrycode', { countryCode: normalizedCountryCode, offset, limit, sortBy });

    const cached = this.getCachedData<RadioStation[]>(cacheKey);
    if (cached && cached.length > 0) {
      return cached;
    }

    try {
      // First get total count without any limits
      const totalCountResults = await this.makeRequest<RadioStation[]>('/stations/search', {
        countrycode: normalizedCountryCode,
        hidebroken: true
      });

      // Then get paginated results for display
      const paginatedResults = await this.makeRequest<RadioStation[]>('/stations/search', {
        countrycode: normalizedCountryCode,
        offset: offset.toString(),
        limit: limit.toString(),
        hidebroken: true,
        order: sortBy,
        reverse: true
      });

      // If we got results, cache them
      if (paginatedResults && paginatedResults.length > 0) {
        logger.success(`✨ Found ${totalCountResults.length} total stations for country ${normalizedCountryCode}`);

        // Store the total count in stationCounts
        this.setCachedData(`country_count_${normalizedCountryCode}`, totalCountResults.length, CACHE_DURATIONS.COUNTRY);

        // Cache the paginated results
        this.setCachedData(cacheKey, paginatedResults, CACHE_DURATIONS.COUNTRY);
        return paginatedResults;
      }

      return [];
    } catch (error) {
      logger.error(`Failed to fetch stations for country ${normalizedCountryCode}:`);
      return [];
    }
  }

  /**
   * Gets a comprehensive list of countries with statistics, handling duplicates and sorting.
   * Used internally for station filtering and tag-based operations.
   * @returns Promise<CountryInfo[]> Array of countries with station counts, sorted by popularity
   */
  public async getCountriesWithStats(): Promise<CountryInfo[]> {
    const cacheKey = 'countries';

    try {
      // Use makeRequest instead of fetchWithCache
      const response = await this.makeRequest<CountryInfo[]>(
        'countries',
        {}
      );

      // Cache the response manually
      if (response.length > 0) {
        this.setCachedData(cacheKey, response, CACHE_DURATIONS.STANDARD);
      }

      // Combine duplicate country codes (uppercase and lowercase)
      const countryMap = new Map<string, CountryInfo>();

      response.forEach(country => {
        const upperName = country.name.toUpperCase();
        const existing = countryMap.get(upperName);

        if (existing) {
          countryMap.set(upperName, {
            name: upperName,
            stationcount: existing.stationcount + country.stationcount
          });
        } else {
          countryMap.set(upperName, {
            name: upperName,
            stationcount: country.stationcount
          });
        }
      });

      // Convert back to array and sort by station count
      return Array.from(countryMap.values())
        .sort((a, b) => b.stationcount - a.stationcount);
    } catch (error) {
      logger.error('Failed to fetch countries:');
      throw error;
    }
  }

  /**
   * Gets a basic list of countries with ISO codes and station counts.
   * Used primarily for analytics and display purposes.
   * @returns Promise<Array<{ name: string; iso_3166_1: string; stationcount: number }>>
   */
  public async getCountriesBasic(): Promise<Array<{ name: string; iso_3166_1: string; stationcount: number }>> {
    const response = await this.makeRequest<Array<{ name: string; iso_3166_1: string; stationcount: number }>>('/countries');
    return response;
  }

  public async getStationsByTag(tag: string, offset: number = 0, limit: number = 100): Promise<RadioStation[]> {
    // Check if the tag is a country code (2 letters)
    if (tag.length === 2) {
      const countries = await this.getCountriesWithStats();
      const isCountry = countries.some(country =>
        country.name.toUpperCase() === tag.toUpperCase()
      );

      if (isCountry) {
        return this.getStationsByCountry(tag, offset, limit);
      }
    }

    const normalizedTag = encodeURIComponent(tag.toLowerCase());
    const cacheKey = this.getCacheKey(`/stations/bytag/${normalizedTag}`, { offset, limit });

    // The result was being written to this key and never read back, so every home
    // page load made a live request per row no matter how recently it had asked.
    const cached = this.getCachedData<RadioStation[]>(cacheKey);
    if (cached && cached.length > 0) return cached;

    try {
      const response = await this.makeRequest<RadioStation[]>(
        `/stations/bytag/${normalizedTag}`,
        {
          offset: offset.toString(),
          limit: limit.toString(),
          hidebroken: 'true',
          order: 'clickcount',
          reverse: 'true'
        }
      );

      const stations = response.map(station => this.parseStationTags(station));
      if (stations.length > 0) {
        logger.info(`✨ Found ${stations.length} stations with tag: ${tag}`);
        this.setCachedData(cacheKey, stations, CACHE_DURATIONS.STANDARD);
      }
      return stations;

    } catch (error) {
      logger.error(`Failed to fetch stations for tag ${tag}:`);

      // Throwing here took the whole home page down. The rows are loaded with a
      // Promise.all over six tags, so one failed request rejected the batch and
      // the page showed "Failed to load radio stations" — while the full
      // catalogue of 60,000+ stations sat unused in the pre-built cache.
      const local = this.stationsByTagFromCatalogue(tag, offset, limit);
      if (local.length > 0) {
        logger.info(`📦 Serving ${local.length} "${tag}" stations from the local catalogue`);
        return local;
      }
      return [];
    }
  }

  /**
   * Answer a tag query from the pre-built catalogue already in memory.
   *
   * Radio Browser's own ordering is by clickcount, so this matches it; the rows
   * end up looking the same whether they came from the API or from here.
   */
  private stationsByTagFromCatalogue(tag: string, offset: number, limit: number): RadioStation[] {
    const needle = tag.trim().toLowerCase();
    if (!needle || this.allStationsCache.length === 0) return [];

    const matches = this.allStationsCache.filter((station) => {
      const raw = Array.isArray(station.tags) ? station.tags.join(',') : String(station.tags || '');
      return raw.toLowerCase().split(',').some((entry) => entry.trim() === needle);
    });

    return matches
      .sort((a, b) => (b.clickcount || 0) - (a.clickcount || 0))
      .slice(offset, offset + limit);
  }

  private async getStationsByCountry(country: string, offset: number = 0, limit: number = 100): Promise<RadioStation[]> {
    const normalizedCountry = country.toUpperCase();
    const cacheKey = this.getCacheKey(`stations/bycountry/${normalizedCountry}`, { offset, limit });

    try {
      const response = await this.makeRequest<RadioStation[]>(
        `stations/bycountry/${normalizedCountry}`,
        {
          offset: offset.toString(),
          limit: limit.toString(),
          hidebroken: 'true',
          order: 'clickcount',
          reverse: 'true'
        }
      );

      const stations = response.map(station => this.parseStationTags(station));
      if (stations.length > 0) {
        logger.info(`✨ Found ${stations.length} stations from country ${country}`);
        this.setCachedData(cacheKey, stations, CACHE_DURATIONS.STANDARD);
      }
      return stations;
    } catch (error) {
      logger.error(`Failed to fetch stations for country ${country}:`);
      throw error;
    }
  }

  public async getRecentlyAddedStations(limit: number = 100): Promise<RadioStation[]> {
    const cacheKey = this.getCacheKey('/stations/lastchange', { limit });
    const cached = this.getCachedData<RadioStation[]>(cacheKey);
    if (cached) return cached;

    try {
      const stations = await this.makeRequest<RadioStation[]>('/stations/lastchange', {
        limit: Math.min(limit, BATCH_SIZE),
        hidebroken: true
      });
      this.setCachedData(cacheKey, stations, CACHE_DURATIONS.STATIC); // Cache for 24 hours only
      return stations;
    } catch (error) {
      logger.error('Failed to fetch recently added stations:');
      return this.allStationsCache
        .sort((a, b) => new Date(b.lastchangetime).getTime() - new Date(a.lastchangetime).getTime())
        .slice(0, limit);
    }
  }

  public async getPopularTags(limit: number = 100): Promise<Tag[]> {
    // If we have cached popular tags, use them
    if (this.popularTagsCache.length > 0) {
      return this.popularTagsCache.slice(0, limit);
    }

    const cacheKey = this.getCacheKey('/tags', { limit });
    const cached = this.getCachedData<Tag[]>(cacheKey);
    if (cached) return cached;

    try {
      const tags = await this.makeRequest<Array<{ name: string; stationcount: number }>>('/tags', {
        limit,
        order: 'stationcount',
        reverse: true
      });

      const processedTags = tags
        .map(tag => {
          const normalizedName = this.normalizeTag(tag.name);
          if (!normalizedName) return null;

          return {
            name: normalizedName,
            count: tag.stationcount,
            slug: normalizedName.replace(/\s+/g, '-'),
            type: this.categorizeTag(normalizedName)
          };
        })
        .filter((tag): tag is Tag => tag !== null);

      this.setCachedData(cacheKey, processedTags, CACHE_DURATIONS.STATIC);
      return processedTags;
    } catch (error) {
      logger.error('Failed to fetch popular tags:');
      return this.popularTagsCache;
    }
  }

  public async searchStations(params: StationSearchParams): Promise<RadioStation[]> {
    const cacheKey = this.getCacheKey('/stations/search', params);

    // Always skip cache for name searches to ensure fresh results
    if (params.name) {
      this.cache.delete(cacheKey);
    }

    try {
      // If we have a search term, try multiple search strategies
      if (params.name) {
        const searchTerm = params.name.trim().toLowerCase();
        const normalizedSearch = this.normalizeSearchTerm(searchTerm);

        // Try exact name match first
        const exactMatches = await this.makeRequest<RadioStation[]>('/stations/search', {
          ...params,
          name: searchTerm,
          nameExact: true,
          offset: '0',
          limit: '200',
          hidebroken: true,
          order: 'clickcount',
          reverse: true
        });

        // Then try partial name match
        const partialMatches = await this.makeRequest<RadioStation[]>('/stations/search', {
          ...params,
          name: searchTerm,
          nameExact: false,
          offset: '0',
          limit: '200',
          hidebroken: true,
          order: 'clickcount',
          reverse: true
        });

        // Try tag-based search
        const tagMatches = await this.makeRequest<RadioStation[]>('/stations/search', {
          ...params,
          tagList: normalizedSearch,
          offset: '0',
          limit: '200',
          hidebroken: true,
          order: 'clickcount',
          reverse: true
        });

        // Combine and deduplicate results
        const allResults = new Map<string, RadioStation & { searchScore?: number }>();

        // Helper function to calculate name match score
        const getNameMatchScore = (stationName: string, searchTerm: string) => {
          const name = stationName.toLowerCase();
          if (name === searchTerm) return 5; // Exact match
          if (name.startsWith(searchTerm)) return 4; // Starts with
          if (name.includes(` ${searchTerm}`)) return 3; // Whole word match
          if (name.includes(searchTerm)) return 2; // Partial match
          return 1; // Tag match
        };

        // Add exact matches first (highest priority)
        exactMatches.forEach(station => {
          const score = getNameMatchScore(station.name, searchTerm);
          allResults.set(station.stationuuid, {
            ...station,
            searchScore: score * 2 // Double score for exact matches
          });
        });

        // Add partial matches
        partialMatches.forEach(station => {
          if (!allResults.has(station.stationuuid)) {
            const score = getNameMatchScore(station.name, searchTerm);
            allResults.set(station.stationuuid, {
              ...station,
              searchScore: score
            });
          }
        });

        // Add tag matches
        tagMatches.forEach(station => {
          if (!allResults.has(station.stationuuid)) {
            allResults.set(station.stationuuid, {
              ...station,
              searchScore: 1
            });
          }
        });

        // Convert to array and sort by score and listener count
        const results = Array.from(allResults.values())
          .sort((a, b) => {
            // First sort by search score
            if ((b.searchScore || 0) !== (a.searchScore || 0)) {
              return (b.searchScore || 0) - (a.searchScore || 0);
            }
            // Then by listener count
            return (b.clickcount || 0) - (a.clickcount || 0);
          });

        // Cache the search results briefly
        this.setCachedData(cacheKey, results, 5000); // Cache for 5 seconds

        return results;
      }

      // For non-search queries (e.g., filtering by tags, country, etc.)
      const stations = await this.makeRequest<RadioStation[]>('/stations/search', {
        ...params,
        offset: params.offset?.toString(),
        limit: Math.min(params.limit || BATCH_SIZE, BATCH_SIZE).toString(),
        hidebroken: params.hidebroken ? 'true' : 'false',
        order: params.order || 'clickcount',
        reverse: true
      });

      return stations;
    } catch (error) {
      logger.error('Failed to search stations:');
      return [];
    }
  }

  // Helper method to normalize search terms
  private normalizeSearchTerm(term: string): string {
    // Common variations and abbreviations
    const variations: { [key: string]: string[] } = {
      'kisstory': ['kiss', 'kisstory radio', 'kiss fm'],
      'rnb': ['r&b', 'rhythm and blues', 'rhythm & blues'],
      'dance': ['edm', 'electronic dance', 'club'],
      'pop': ['popular', 'top 40', 'top40'],
      'rock': ['alternative rock', 'alt rock', 'classic rock'],
      'classical': ['classic', 'orchestra', 'symphony'],
      'jazz': ['blues', 'jazz blues', 'smooth jazz'],
      'news': ['talk', 'news talk', 'talk radio'],
      'country': ['country music', 'country western', 'country & western'],
      'hits': ['top hits', 'hot hits', 'hit music', 'popular hits'],
      'oldies': ['classic hits', 'retro', 'vintage'],
      'chill': ['chillout', 'lounge', 'ambient', 'relaxation']
    };

    let normalized = term.toLowerCase().trim();

    // Replace special characters
    normalized = normalized.replace(/[&+]/g, ' and ');

    // Check for known variations
    for (const [main, variants] of Object.entries(variations)) {
      if (variants.includes(normalized) || normalized.includes(main)) {
        return variants.concat(main).join(',');
      }
    }

    // Handle decade variations (80s, 80's, eighties)
    const decadeMatch = normalized.match(/(?:19|20)?(\d{2})(?:s|'s)?/);
    if (decadeMatch) {
      const decade = decadeMatch[1];
      return `${decade}s,${decade}'s,nineteen ${decade}s`;
    }

    return normalized;
  }

  // Helper method to sort stations
  private sortStations(stations: RadioStation[], order: string, reverse: boolean = false): RadioStation[] {
    const sorted = [...stations].sort((a, b) => {
      switch (order) {
        case 'clickcount':
          return (b.clickcount || 0) - (a.clickcount || 0);
        case 'votes':
          return (b.votes || 0) - (a.votes || 0);
        case 'name':
          return a.name.localeCompare(b.name);
        case 'bitrate':
          return (b.bitrate || 0) - (a.bitrate || 0);
        case 'clicktrend':
          return (b.clicktrend || 0) - (a.clicktrend || 0);
        case 'random':
          return Math.random() - 0.5;
        default:
          return 0;
      }
    });

    return reverse ? sorted.reverse() : sorted;
  }

  /**
   * Report a station click to the API
   */
  public async reportStationClick(stationUuid: string): Promise<void> {
    if (!stationUuid) {
      logger.error('Cannot report click: Invalid station UUID');
      return;
    }

    try {
      await this.makeRequest<any>(`/url/${stationUuid}`);
      logger.info(`Reported click for station ${stationUuid}`);
    } catch (error) {
      logger.error('Failed to report station click', error);
    }
  }

  public async getAllStations(): Promise<RadioStation[]> {
    try {
      const stations = await this.makeRequest<RadioStation[]>('/stations', {
        hidebroken: true
      });

      // Cache the results
      this.cacheStations(stations);

      return stations;
    } catch (error) {
      logger.error('Failed to fetch all stations:');
      // Try to return cached data if available
      const cached = await this.getCachedStations();
      if (cached.length > 0) {
        return cached;
      }
      throw error;
    }
  }

  /**
   * How many stations this app actually ships, from the build-time catalogue.
   *
   * Radio Browser's /stats reports *their* global totals, which is why the app
   * advertised 60k+ while its own catalogue held 23,500 — a build that only
   * reached part of the API still showed the full upstream figure. Returns null
   * if the metadata isn't available, so callers can fall back.
   */
  private async getCatalogueCount(): Promise<number | null> {
    const cacheKey = 'catalogue_count';
    const cached = this.getCachedData<number>(cacheKey);
    if (typeof cached === 'number') return cached;

    try {
      const response = await fetch('/cache/metadata.json', { cache: 'no-cache' });
      if (!response.ok) return null;
      const metadata = await response.json();
      const count = Number(metadata?.stationCount);
      if (!Number.isFinite(count) || count <= 0) return null;

      this.setCachedData(cacheKey, count, CACHE_DURATIONS.STANDARD);
      return count;
    } catch (error) {
      logger.warning('Could not read local catalogue metadata for stats');
      return null;
    }
  }

  async getStats(): Promise<StatsResponse> {
    try {
      const cacheKey = 'stats';
      const cached = this.getCachedData<StatsResponse>(cacheKey);

      if (cached) {
        return cached;
      }

      const stats = await this.makeRequest<StatsResponse>('/stats');

      // Report our own station count rather than Radio Browser's global one, so
      // every page that shows "stations" agrees with what users can actually
      // browse. Countries/languages/tags stay upstream — those are accurate
      // descriptions of the catalogue regardless of how much of it we hold.
      const catalogueCount = await this.getCatalogueCount();
      const merged: StatsResponse = catalogueCount === null
        ? stats
        : { ...stats, stations: catalogueCount };

      this.setCachedData(cacheKey, merged, CACHE_DURATIONS.STANDARD);
      return merged;
    } catch (error) {
      logger.error('Failed to get stats', error);

      // Upstream unreachable: still report our own catalogue rather than throwing
      // the whole stats panel away.
      const catalogueCount = await this.getCatalogueCount();
      if (catalogueCount !== null) {
        return {
          stations: catalogueCount,
          stations_broken: 0,
          countries: 0,
          languages: 0,
          tags: 0,
          clicks_last_hour: 0,
          clicks_last_day: 0,
          categories: 0,
        };
      }
      throw error;
    }
  }

  async getLanguages(): Promise<Array<{ name: string; stationcount: number }>> {
    const response = await this.makeRequest<Array<{ name: string; stationcount: number }>>('/languages');
    return response;
  }

  async getCodecs(): Promise<Array<{ name: string; stationcount: number }>> {
    const response = await this.makeRequest<Array<{ name: string; stationcount: number }>>('/codecs');
    return response;
  }

  public async getStationById(stationUuid: string): Promise<RadioStation | null> {
    if (!stationUuid) {
      logger.error('Cannot get station: Invalid UUID');
      return null;
    }

    try {
      const cacheKey = `station:${stationUuid}`;
      const cached = this.getCachedData<RadioStation>(cacheKey);
      if (cached) {
        logger.info(`Found station ${stationUuid} in cache`);
        return cached;
      }

      logger.info(`Fetching station ${stationUuid} from API`);
      const response = await this.makeRequest<RadioStation[]>('/stations/byuuid', {
        uuids: stationUuid,
        hidebroken: true
      });

      if (!response || response.length === 0) {
        logger.warning(`Station ${stationUuid} not found`);
        // Try to find station in all stations cache
        const cachedStation = this.allStationsCache.find(s => s.stationuuid === stationUuid);
        if (cachedStation) {
          logger.info(`Found station ${stationUuid} in all stations cache`);
          return cachedStation;
        }
        return null;
      }

      const station = this.parseStationTags(response[0]);
      this.setCachedData(cacheKey, station, CACHE_DURATIONS.DYNAMIC);
      logger.info(`Successfully fetched station: ${station.name}`);
      return station;
    } catch (error) {
      logger.error(`Failed to fetch station ${stationUuid}:`);
      // Try to find station in all stations cache as fallback
      const cachedStation = this.allStationsCache.find(s => s.stationuuid === stationUuid);
      if (cachedStation) {
        logger.info(`Found station ${stationUuid} in all stations cache after error`);
        return cachedStation;
      }
      return null;
    }
  }

  public async voteForStation(stationUuid: string): Promise<void> {
    if (!stationUuid) {
      logger.error('Cannot vote: Invalid station UUID');
      return;
    }

    try {
      const response = await this.makeRequest<{ ok: boolean; message: string }>(`/vote/${stationUuid}`);

      if (response?.ok) {
        logger.success(`✨ Voted for station ${stationUuid}`);
      } else {
        throw new Error('Vote failed: ' + JSON.stringify(response));
      }
    } catch (error: unknown) {
      // Check if it's an axios error with response status
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 429) {
          logger.warning('⚠️ Please wait before voting again (10 minute cooldown)');
          throw new Error('Please wait before voting again');
        } else {
          logger.error(`Vote failed with status ${error.response.status}:`);
          throw new Error(`Vote failed: ${error.response.status}`);
        }
      }
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Failed to vote for station:');
      throw new Error(`Failed to vote: ${errorMessage}`);
    }
  }

  /**
   * Rotate to the next server in the list
   * This is used when a server is unresponsive
   */
  private rotateToNextServer(): void {
    if (!this.currentServer || this.serverList.length <= 1) return;

    // Move to the next server in the list
    const currentIndex = this.serverList.indexOf(this.currentServer);
    const nextIndex = (currentIndex + 1) % this.serverList.length;
    this.currentServer = this.serverList[nextIndex];
  }

  /**
   * Initialize Radio Browser service and fetch initial data
   */
  public async initialize() {
    logger.info('🎵 Initializing Radio Browser Service...');

    try {
      // First try to load cached data for quick startup
      const hasCachedData = await this.loadCachedData();

      // Initialize server connection
      await this.initializeServers();

      if (!this.currentServer) {
        logger.error('Failed to initialize any Radio Browser servers');
        // Still try to use cache if available
        return hasCachedData;
      }

      if (!hasCachedData) {
        // If no cached data, fetch initial data
        logger.info('No cached data found, fetching fresh data...');
        try {
          // Set a timeout for the initial fetch to prevent hanging
          const fetchPromise = this.updateDataInBackground();
          const timeoutPromise = new Promise<boolean>((resolve) => {
            setTimeout(() => {
              logger.warning('Initial data fetch timed out, will retry in background');
              resolve(false);
            }, 5000); // 5 second timeout for initial fetch
          });

          await Promise.race([fetchPromise, timeoutPromise]);
        } catch (error) {
          logger.error('Failed to fetch initial data, will retry in background');
        }
      } else {
        // If we have cached data, update in background after a delay
        logger.info(`Using ${this.allStationsCache.length} cached stations`);
        setTimeout(() => this.updateDataInBackground(), 5000);
      }

      // Start background refresh
      this.startBackgroundRefresh();

      // Return success if we have either servers or cache
      return this.currentServer !== null || this.allStationsCache.length > 0;
    } catch (error) {
      logger.error(`Failed to initialize radio service: ${error instanceof Error ? error.message : String(error)}`);
      // Return true if we at least have cached data
      return this.allStationsCache.length > 0;
    }
  }

  private startBackgroundRefresh() {
    // Refresh data every 30 minutes
    setInterval(() => this.updateDataInBackground(), 30 * 60 * 1000);
  }

  private async loadCachedData() {
    try {
      // Try both pre-built cache and localStorage to ensure maximum resilience
      let stations: RadioStation[] = [];

      // First try pre-built cache
      try {
        const response = await fetch('/cache/stations.json');
        if (response.ok) {
          stations = await response.json();
          logger.success(`✨ Found ${stations.length} stations in pre-built cache`);
        } else {
          throw new Error('Pre-built cache unavailable or invalid');
        }
      } catch (prebuiltError) {
        logger.warning(`Failed to load pre-built cache, trying localStorage: ${prebuiltError}`);

        // Try localStorage as fallback
        try {
          // Check if we have chunked storage
          const chunkCount = parseInt(localStorage.getItem('stations_cache_chunks') || '0', 10);

          if (chunkCount > 0) {
            const allStations: RadioStation[] = [];

            for (let i = 0; i < chunkCount; i++) {
              const chunk = localStorage.getItem(`stations_cache_${i}`);
              if (chunk) {
                const parsedChunk = JSON.parse(chunk);
                allStations.push(...parsedChunk);
              }
            }

            if (allStations.length > 0) {
              stations = allStations;
              logger.success(`✨ Loaded ${stations.length} stations from localStorage chunks`);
            }
          } else {
            // Try single storage item
            const storedData = localStorage.getItem('radio_stations');
            if (storedData) {
              const { stations: storedStations } = JSON.parse(storedData);
              if (Array.isArray(storedStations) && storedStations.length > 0) {
                stations = storedStations;
                logger.success(`✨ Loaded ${stations.length} stations from localStorage`);
              }
            }
          }
        } catch (localStorageError) {
          logger.warning(`Failed to load from localStorage: ${localStorageError}`);
        }
      }

      // If we loaded stations from any source, process and cache them
      if (stations.length > 0) {
        // Process stations before storing
        const processedStations = stations.map((station: RadioStation) => this.parseStationTags(station));
        this.allStationsCache = processedStations;

        // Try to store in localStorage for future use
        try {
          this.cacheStations(processedStations);
        } catch (storageError: unknown) {
          const errorName = storageError instanceof Error ? storageError.name : 'Unknown';
          logger.warning(`Failed to cache stations to localStorage (${errorName})`);
        }

        return true;
      }

      return false;
    } catch (error) {
      logger.error(`Failed to load cached data: ${error instanceof Error ? error.message : String(error)}`);
      return false;
    }
  }

  private async updateDataInBackground() {
    try {
      // Ensure we have a valid server first
      if (!this.currentServer) {
        await this.initializeServers();
        if (!this.currentServer) {
          throw new Error('No valid server available');
        }
      }

      // Use a Promise.race with timeout to prevent hanging
      const fetchPromise = this.makeRequest<RadioStation[]>('/stations/search', {
        hidebroken: true,
        limit: 500 // Increased for better initial data
      });

      const timeoutPromise = new Promise<RadioStation[]>((_, reject) => {
        setTimeout(() => reject(new Error('Timeout fetching stations')), 10000);
      });

      // Wait for fetch or timeout
      const stations = await Promise.race([fetchPromise, timeoutPromise]);

      if (stations && stations.length > 0) {
        // Replace or merge with existing cache based on what's better
        if (this.allStationsCache.length === 0) {
          // No existing cache, use new data directly
          this.allStationsCache = stations;
        } else if (stations.length >= this.allStationsCache.length) {
          // New data has more stations, likely more complete
          this.allStationsCache = stations;
        } else {
          // Merge strategy - update existing entries with fresh data
          const stationMap = new Map<string, RadioStation>();

          // Add all existing stations to map
          this.allStationsCache.forEach(station => {
            stationMap.set(station.stationuuid, station);
          });

          // Update with newer data
          stations.forEach(station => {
            stationMap.set(station.stationuuid, station);
          });

          this.allStationsCache = Array.from(stationMap.values());
        }

        // Cache the updated stations
        this.cacheStations(this.allStationsCache);

        logger.success(`🔄 Background data update complete - ${this.allStationsCache.length} stations available`);
        return true;
      } else {
        logger.warning('Background update received empty data');
        return false;
      }
    } catch (error) {
      logger.error(`Failed to update data in background: ${error instanceof Error ? error.message : String(error)}`);

      // Try rotating to a different server on failure
      this.rotateToNextServer();
      return false;
    }
  }

  private cacheStations(stations: RadioStation[]) {
    try {
      // Cache in memory
      this.stationCache.set('all_stations', {
        data: stations,
        timestamp: Date.now()
      });

      if (typeof localStorage === 'undefined') {
        return;
      }

      const persistedStations = stations.map(station => this.getPersistedStation(station));

      if (persistedStations.length === 0) {
        this.clearBulkStationCache();
        localStorage.removeItem('radio_stations');
        localStorage.removeItem('radio_stations_reduced');
        return;
      }
      const singlePayload = JSON.stringify({
        stations: persistedStations,
        timestamp: Date.now()
      });
      const singlePayloadSize = this.getStringSizeInBytes(singlePayload);

      // Try storing in a single entry if it fits comfortably within the quota
      if (singlePayloadSize <= MAX_LOCAL_STORAGE_BYTES) {
        try {
          this.clearBulkStationCache();
          localStorage.setItem('radio_stations', singlePayload);
          localStorage.removeItem('radio_stations_reduced');
          logger.info(`✅ Cached ${stations.length} stations in single payload (${singlePayloadSize} bytes)`);
          return;
        } catch (storageError: unknown) {
          const errorName = storageError instanceof Error ? storageError.name : 'Unknown';
          logger.warning(`Failed to cache full dataset in single payload (${errorName})`);
        }
      }

      // If the payload is too large for a single entry, attempt chunked storage
      const chunkPayloads = this.prepareChunkPayloads(persistedStations);

      if (!chunkPayloads) {
        this.cacheReducedStationSet(stations);
        return;
      }

      const writtenKeys: string[] = [];

      try {
        this.clearBulkStationCache();
        localStorage.removeItem('radio_stations');

        localStorage.setItem('stations_cache_timestamp', Date.now().toString());
        writtenKeys.push('stations_cache_timestamp');

        localStorage.setItem('stations_cache_chunks', chunkPayloads.length.toString());
        writtenKeys.push('stations_cache_chunks');

        chunkPayloads.forEach(({ key, value }) => {
          localStorage.setItem(key, value);
          writtenKeys.push(key);
        });

        localStorage.removeItem('radio_stations_reduced');
        logger.info(`✅ Cached ${stations.length} stations in ${chunkPayloads.length} chunks`);
      } catch (storageError: unknown) {
        writtenKeys.forEach(key => {
          localStorage.removeItem(key);
        });

        const errorName = storageError instanceof Error ? storageError.name : 'Unknown';
        logger.warning(`Failed to cache full dataset in chunks (${errorName}), falling back to reduced set`);
        this.cacheReducedStationSet(stations);
      }
    } catch (error) {
      logger.error(`Error caching stations: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private cacheReducedStationSet(stations: RadioStation[]) {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }

      this.clearBulkStationCache();
      localStorage.removeItem('radio_stations');

      const sortedStations = [...stations].sort((a, b) => (b.clickcount || 0) - (a.clickcount || 0));
      const reducedSet = sortedStations.slice(0, REDUCED_STATION_CACHE_SIZE).map(station => this.getPersistedStation(station));

      const payload = JSON.stringify({
        stations: reducedSet,
        timestamp: Date.now(),
        isReduced: true
      });

      if (this.getStringSizeInBytes(payload) > MAX_LOCAL_STORAGE_BYTES) {
        logger.warning('Reduced station payload is still too large for localStorage quota, skipping persistent cache');
        localStorage.removeItem('radio_stations_reduced');
        return;
      }

      localStorage.setItem('radio_stations_reduced', payload);
      logger.info(`✅ Cached reduced set of ${reducedSet.length} stations`);
    } catch (error) {
      logger.error(`Failed to cache even reduced station set: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private prepareChunkPayloads(stations: PersistedStation[]): { key: string; value: string }[] | null {
    if (stations.length === 0) {
      return [];
    }

    const chunkCount = Math.ceil(stations.length / STATION_STORAGE_CHUNK_SIZE);

    if (chunkCount > MAX_STORAGE_CHUNK_COUNT) {
      // Expected, not a fault: the full catalogue is ~62,000 stations and
      // localStorage is a 5 MB budget, so this branch is always taken. The real
      // catalogue is the pre-built cache file; what gets stored here is only a
      // small offline fallback for when that file cannot be fetched.
      logger.info(`Catalogue too large for localStorage (${chunkCount} chunks > ${MAX_STORAGE_CHUNK_COUNT}); keeping a reduced offline set`);
      return null;
    }

    let totalBytes = 0;
    const chunkPayloads: { key: string; value: string }[] = [];

    for (let i = 0; i < chunkCount; i++) {
      const chunk = stations.slice(i * STATION_STORAGE_CHUNK_SIZE, (i + 1) * STATION_STORAGE_CHUNK_SIZE);
      const serializedChunk = JSON.stringify(chunk);
      totalBytes += this.getStringSizeInBytes(serializedChunk);

      if (totalBytes > MAX_LOCAL_STORAGE_BYTES) {
        logger.warning(`Station cache chunks would require ${totalBytes} bytes (> ${MAX_LOCAL_STORAGE_BYTES}), skipping bulk cache`);
        return null;
      }

      chunkPayloads.push({ key: `stations_cache_${i}`, value: serializedChunk });
    }

    totalBytes += this.getStringSizeInBytes(Date.now().toString());
    totalBytes += this.getStringSizeInBytes(chunkCount.toString());

    if (totalBytes > MAX_LOCAL_STORAGE_BYTES) {
      logger.warning(`Station cache metadata pushes size over limit (${totalBytes} bytes), skipping bulk cache`);
      return null;
    }

    return chunkPayloads;
  }

  private clearBulkStationCache() {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const chunkCount = parseInt(localStorage.getItem('stations_cache_chunks') || '0', 10);

    for (let i = 0; i < chunkCount; i++) {
      localStorage.removeItem(`stations_cache_${i}`);
    }

    localStorage.removeItem('stations_cache_chunks');
    localStorage.removeItem('stations_cache_timestamp');
  }

  private getPersistedStation(station: RadioStation): PersistedStation {
    const persisted: PersistedStation = {
      stationuuid: station.stationuuid,
      changeuuid: station.changeuuid,
      name: station.name,
      url: station.url,
      url_resolved: station.url_resolved,
      votes: station.votes || 0,
      lastchangetime: station.lastchangetime,
      codec: station.codec,
      bitrate: station.bitrate,
      hls: station.hls,
      lastcheckok: station.lastcheckok,
      lastchecktime: station.lastchecktime,
      ssl_error: station.ssl_error,
      clickcount: station.clickcount || 0,
      clicktrend: station.clicktrend || 0
    };

    if (station.favicon) persisted.favicon = station.favicon;
    if (station.tags) persisted.tags = station.tags;
    if (station.country) persisted.country = station.country;
    if (station.countrycode) persisted.countrycode = station.countrycode;
    if (station.state) persisted.state = station.state;
    if (station.language) persisted.language = station.language;
    if (station.homepage) persisted.homepage = station.homepage;
    if (station.lastcheckoktime) persisted.lastcheckoktime = station.lastcheckoktime;
    if (station.clicktimestamp) persisted.clicktimestamp = station.clicktimestamp;
    if (typeof station.geo_lat !== 'undefined') persisted.geo_lat = station.geo_lat;
    if (typeof station.geo_long !== 'undefined') persisted.geo_long = station.geo_long;

    return persisted;
  }

  private getStringSizeInBytes(value: string): number {
    if (typeof TextEncoder !== 'undefined') {
      if (!this.textEncoder) {
        this.textEncoder = new TextEncoder();
      }

      return this.textEncoder.encode(value).length;
    }

    // Fallback estimate if TextEncoder is unavailable
    return value.length;
  }

  public async getCachedStations(): Promise<RadioStation[]> {
    try {
      // Try to get from memory cache first
      const cached = this.stationCache.get('all_stations');
      if (cached && Date.now() - cached.timestamp < CACHE_DURATIONS.STATIC) {
        logger.info('Using cached stations from memory');
        return cached.data;
      }

      // Try to get from localStorage
      const storedData = localStorage.getItem('radio_stations');
      if (storedData) {
        const { stations, timestamp } = JSON.parse(storedData);
        if (Date.now() - timestamp < CACHE_DURATIONS.STATIC) {
          logger.info('Using cached stations from storage');
          // Update memory cache
          this.stationCache.set('all_stations', { data: stations, timestamp });
          return stations;
        }
      }

      return [];
    } catch (error) {
      logger.error('Error getting cached stations:');
      return [];
    }
  }

  /**
   * Get stations from multiple countries in a region
   * @param region Array of country codes
   * @param offset Pagination offset
   * @param limit Number of results to return
   * @param sortBy Sorting field
   */
  public async getStationsByRegion(region: string[], offset: number = 0, limit: number = 100, sortBy: string = 'clickcount'): Promise<RadioStation[]> {
    const cacheKey = this.getCacheKey('stations/byregion', { region: region.join(','), offset, limit, sortBy });
    const cached = this.getCachedData<RadioStation[]>(cacheKey);
    if (cached && cached.length > 0) {
      return cached;
    }

    try {
      // Fetch stations for all countries in the region in parallel
      const stationPromises = region.map(countryCode =>
        this.makeRequest<RadioStation[]>('/stations/search', {
          countrycode: countryCode.toUpperCase(),
          offset: '0',
          limit: '100', // Get more stations per country for better sorting
          hidebroken: true,
          order: sortBy,
          reverse: true
        })
      );

      const results = await Promise.all(stationPromises);

      // Combine and sort all stations
      const allStations = results.flat();

      // Apply sorting
      const sortedStations = this.sortStations(allStations, sortBy, true)
        .slice(offset, offset + limit);

      if (sortedStations.length > 0) {
        this.setCachedData(cacheKey, sortedStations, CACHE_DURATIONS.COUNTRY);
        return sortedStations;
      }

      return [];
    } catch (error: unknown) {
      // Try each country individually if the combined request fails
      try {
        logger.warning('Combined region request failed, trying individual countries...');

        // Try each country one at a time with fallback
        let allStations: RadioStation[] = [];

        for (const countryCode of region) {
          try {
            const stations = await this.makeRequest<RadioStation[]>('/stations/search', {
              countrycode: countryCode.toUpperCase(),
              offset: '0',
              limit: '50', // Smaller batch per country
              hidebroken: true,
              order: sortBy,
              reverse: true
            });

            allStations = [...allStations, ...stations];
          } catch (countryError) {
            logger.warning(`Failed to fetch stations for country ${countryCode}, continuing with others`);
          }
        }

        // Return what we got, even if partial
        const sortedStations = this.sortStations(allStations, sortBy, true)
          .slice(offset, offset + limit);

        if (sortedStations.length > 0) {
          return sortedStations;
        }

        return [];
      } catch (fallbackError) {
        logger.error('Failed to fetch stations for region:', fallbackError);
        return [];
      }
    }
  }

  // Update the mapFallbackStations method to match RadioStation interface
  private mapFallbackStations(): RadioStation[] {
    return FALLBACK_STATIONS.map((station, index) => ({
      stationuuid: `fallback-${index}`,
      changeuuid: `fallback-change-${index}`,
      name: station.name,
      url: station.url,
      url_resolved: station.url,
      favicon: station.favicon || '',
      tags: station.tags || '',
      country: station.country || '',
      countrycode: station.countryCode || '',
      state: '',
      language: 'English',
      votes: 0,
      codec: station.codec || 'MP3',
      bitrate: station.bitrate || 128,
      lastcheckok: 1,
      lastchangetime: new Date().toISOString(),
      lastchecktime: new Date().toISOString(),
      lastcheckoktime: new Date().toISOString(),
      clickcount: 0,
      clicktrend: 0,
      ssl_error: 0,
      hls: 0,
      geo_lat: undefined,
      geo_long: undefined
    }));
  }

  // Let's update the fetchStations method in the RadioBrowserService class
  async fetchStations(params: StationSearchParams): Promise<RadioStation[]> {
    try {
      // Fetch stations using our makeRequest method
      try {
        const stations = await this.makeRequest<RadioStation[]>('/stations/search', params);

        if (stations && stations.length > 0) {
          return stations;
        }

        // If we get here, the API returned no stations
        logger.warning('Radio-Browser API returned no stations, using fallback stations');
        return this.mapFallbackStations();
      } catch (error) {
        // If there's an error (API down, network issue, etc.), log and use fallback
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logger.warning(`Failed to fetch stations from Radio-Browser API: ${errorMessage}, using fallback stations`);
        return this.mapFallbackStations();
      }
    } catch (error) {
      // If we can't even make the request, use fallback stations
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.warning(`Failed to request from Radio-Browser API: ${errorMessage}, using fallback stations`);
      return this.mapFallbackStations();
    }
  }

  /**
   * Get a list of all countries
   */
  public async getCountries(params: { order?: string; reverse?: boolean } = {}): Promise<CountryInfo[]> {
    try {
      const cacheKey = `countries:${JSON.stringify(params)}`;
      const cached = this.getCachedData<CountryInfo[]>(cacheKey);

      if (cached) {
        return cached;
      }

      const countries = await this.makeRequest<CountryInfo[]>('/countries', params);

      this.setCachedData(cacheKey, countries, CACHE_DURATIONS.COUNTRY);
      return countries;
    } catch (error) {
      logger.error('Failed to get countries', error);
      return [];
    }
  }
}

export const radioService = RadioBrowserService.getInstance();
export default radioService; 